/**
 * Domain Validator Utility
 * 
 * Loads allowed domains from Firestore and validates email addresses
 * against domain whitelist and regex patterns.
 * 
 * Uses unifiedDataService.SystemConfigService for data access.
 */

import { SystemConfigService } from '../firebase/unifiedDataService';
import { Logger } from './logger';

const log = Logger.create('DomainValidator');

// Cache for domains config (refreshed periodically)
let domainsCache = null;
let lastFetch = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Load allowed domains from Firestore
 * @param {boolean} forceRefresh - Force refresh cache
 * @returns {Promise<Object>} Domains configuration
 */
export async function loadAllowedDomains(forceRefresh = false) {
  const now = Date.now();
  
  // Return cached data if valid
  if (!forceRefresh && domainsCache && lastFetch && (now - lastFetch) < CACHE_DURATION) {
    return domainsCache;
  }
  
  try {
    const data = await SystemConfigService.getAllowedDomains();
    
    if (data) {
      domainsCache = data;
      lastFetch = now;
      return domainsCache;
    } else {
      log.warn('No allowed_domains config found in Firestore, using fallback');
      // Fallback to default
      domainsCache = {
        student: [{ domain: 'haw-hamburg.de', enabled: true, emailPattern: '^[a-z]+\\.[a-z]+@haw-hamburg\\.de$' }],
        teacher: [{ domain: 'haw-hamburg.de', enabled: true, emailPattern: '^[a-z]+\\.[a-z]+@haw-hamburg\\.de$' }],
        admin: [{ domain: 'haw-hamburg.de', enabled: true, emailPattern: '^[a-z]+\\.[a-z]+@haw-hamburg\\.de$' }]
      };
      lastFetch = now;
      return domainsCache;
    }
  } catch (error) {
    log.error('Error loading allowed domains:', error);
    // Return fallback
    return {
      student: [{ domain: 'haw-hamburg.de', enabled: true, emailPattern: '^[a-z]+\\.[a-z]+@haw-hamburg\\.de$' }],
      teacher: [{ domain: 'haw-hamburg.de', enabled: true, emailPattern: '^[a-z]+\\.[a-z]+@haw-hamburg\\.de$' }],
      admin: [{ domain: 'haw-hamburg.de', enabled: true, emailPattern: '^[a-z]+\\.[a-z]+@haw-hamburg\\.de$' }]
    };
  }
}

/**
 * Check if email is allowed for a specific role
 * @param {string} email - Email address to check
 * @param {string} role - Role (student, teacher, admin)
 * @returns {Promise<Object>} { allowed: boolean, error: string|null, matchedDomain: Object|null }
 */
export async function validateEmailForRole(email, role = 'student') {
  if (!email || typeof email !== 'string') {
    return { allowed: false, error: 'Bitte gib eine gültige E-Mail-Adresse ein.' };
  }
  
  if (!email.includes('@')) {
    return { allowed: false, error: 'Bitte gib eine gültige E-Mail-Adresse ein.' };
  }
  
  const normalizedEmail = email.toLowerCase().trim();
  const domain = normalizedEmail.split('@')[1];
  
  if (!domain) {
    return { allowed: false, error: 'Bitte gib eine gültige E-Mail-Adresse ein.' };
  }
  
  // Load domains config
  const domainsConfig = await loadAllowedDomains();
  const roleDomains = domainsConfig[role] || [];
  
  // Find matching domain
  const matchedDomain = roleDomains.find(d => d.domain === domain && d.enabled !== false);
  
  if (!matchedDomain) {
    const allowedList = roleDomains.map(d => `@${d.domain}`).join(', ');
    return { 
      allowed: false, 
      error: `Die Domain @${domain} ist nicht erlaubt. Erlaubte Domains: ${allowedList}` 
    };
  }
  
  // Check email pattern if defined
  if (matchedDomain.emailPattern) {
    const pattern = new RegExp(matchedDomain.emailPattern, 'i');
    if (!pattern.test(normalizedEmail)) {
      return {
        allowed: false,
        error: `Die E-Mail-Adresse entspricht nicht dem erforderlichen Format. Beispiel: vorname.nachname@${domain}`,
        matchedDomain
      };
    }
  }
  
  return { allowed: true, error: null, matchedDomain };
}

/**
 * Check if email is allowed for student registration
 * @param {string} email 
 * @returns {Promise<Object>}
 */
export async function isAllowedStudentEmail(email) {
  return validateEmailForRole(email, 'student');
}

/**
 * Check if email is allowed for teacher
 * @param {string} email 
 * @returns {Promise<Object>}
 */
export async function isAllowedTeacherEmail(email) {
  return validateEmailForRole(email, 'teacher');
}

/**
 * Get formatted list of allowed domains for display
 * @param {string} role 
 * @returns {Promise<string[]>}
 */
export async function getAllowedDomainsForRole(role = 'student') {
  const domainsConfig = await loadAllowedDomains();
  const roleDomains = domainsConfig[role] || [];
  return roleDomains
    .filter(d => d.enabled !== false)
    .map(d => `@${d.domain}`);
}

/**
 * Clear cache (useful for testing or manual refresh)
 */
export function clearDomainsCache() {
  domainsCache = null;
  lastFetch = null;
}

export default {
  loadAllowedDomains,
  validateEmailForRole,
  isAllowedStudentEmail,
  isAllowedTeacherEmail,
  getAllowedDomainsForRole,
  clearDomainsCache
};
